<template>
    <div>
        <v-img max-height="400px" src="/profile-bg.jpg" />
        <v-container class="wrapper">
            <div class="avatar">
                <v-img :src="(user && user.avatar) || '/profile-avatar.png'" />
            </div>
            <v-row v-if="dataLoaded" class="form-wrapper">
                <v-col cols="12" md="8">
                    <v-form ref="user-form">
                        <v-row>
                            <v-col cols="12" md="2">
                                <v-select
                                    v-model="userData.prefix"
                                    variant="outlined"
                                    density="compact"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="`${$t('salutation')}*`"
                                    :rules="[requiredRule('salutation')]"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="firstName"
                                    prepend-inner-icon="mdi-account"
                                    variant="outlined"
                                    density="compact"
                                    :label="$t('first_name')"
                                    :rules="validationRules('first_name', [latinRule, requiredRule])"
                                    :disabled="lockEditPrivateClientName"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="lastName"
                                    prepend-inner-icon="mdi-account"
                                    variant="outlined"
                                    density="compact"
                                    :label="$t('last_name')"
                                    :rules="validationRules('last_name', [latinRule, requiredRule])"
                                    :disabled="lockEditPrivateClientName"
                                    required
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <phone-field
                                    v-model="userData.contactPhone"
                                    prepend-inner-icon="mdi-phone"
                                    density="compact"
                                    :label="$t('phone')"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="userData.email"
                                    prepend-inner-icon="mdi-email"
                                    variant="outlined"
                                    density="compact"
                                    :label="$t('email')"
                                    :rules="validationRules('email', [emailRule, requiredRule])"
                                    required
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="3">
                                <v-autocomplete
                                    v-model="userData.citizenshipId"
                                    prepend-inner-icon="mdi-earth"
                                    variant="outlined"
                                    :label="$t('citizenship')"
                                    density="compact"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-autocomplete
                                    v-model="userData.language"
                                    prepend-inner-icon="mdi-web"
                                    variant="outlined"
                                    :label="$t('language')"
                                    density="compact"
                                    clearable
                                    :items="localesItems"
                                    item-title="label"
                                    item-value="value"
                                    required
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-btn color="primary" block :loading="loading" @click="save('settings')">
                                    {{ $t('save_changes') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-col>
                <v-col cols="12" md="4" :class="{'password-form': !$breakpoint.smAndDown}">
                    <v-form ref="password-form">
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="password"
                                    prepend-inner-icon="mdi-email"
                                    variant="outlined"
                                    density="compact"
                                    :label="$t('password')"
                                    :rules="[requiredRule('password')]"
                                    required
                                    type="password"
                                    autocomplete="new-password"
                                    @update:model-value="passwordConfirm = null"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-text-field
                                    v-model="passwordConfirm"
                                    prepend-inner-icon="mdi-email"
                                    variant="outlined"
                                    density="compact"
                                    :label="$t('password_confirm')"
                                    :rules="[
                                        requiredRule('password_confirm'),
                                        v => v === password || `${$t('password_confirm')} ${$t('validation.match')}`,
                                    ]"
                                    required
                                    validate-on="blur"
                                    type="password"
                                    autocomplete="new-password"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn color="primary" block :loading="loadingPassword" @click="save('password')">
                                    {{ $t('save_new_password') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-col>
                <v-col v-if="isB2C && totalLoyaltyPoints !== undefined" cols="12">
                    <v-expansion-panels>
                        <v-expansion-panel>
                            <v-expansion-panel-title>
                                <div class="text-h5 mb-4">
                                    {{ $t('loyalty') }}
                                </div>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <div class="d-flex align-start">
                                    <div class="text-subtitle-2 mb-4">
                                        {{ $t('loyalty_count') }}: {{ totalLoyaltyPoints }}
                                    </div>
                                    <v-dialog v-model="dialog" width="500">
                                        <template v-slot:activator="{props}">
                                            <v-icon color="primary" class="ml-2" v-bind="props">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>

                                        <v-card>
                                            <v-card-title class="text-h5 bg-grey-lighten-2">
                                                {{ $t('loyalty') }}
                                            </v-card-title>

                                            <v-card-text>
                                                <div v-html="$t('about_loyalty')" />
                                                <div class="text-red" v-html="$t('violation_loyalty')" />
                                            </v-card-text>

                                            <v-divider />

                                            <v-card-actions>
                                                <v-spacer />
                                                <v-btn color="primary" @click="dialog = false">
                                                    {{ $t('ok') }}
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-dialog>
                                </div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script lang="ts">
    import {Component, mixins, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
    import PhoneField from '@/components/snippets/forms/PhoneField.vue'
    import {appInstance} from '~/utils/app-accessor'
    import {EventBus, SWITCH_LANGUAGE} from '~/utils/event-bus'
    import {callComponentMethod} from '~/utils/vue-class-helpers'

    @Component({
        components: {PhoneField},
    })
    export class UserSettings extends mixins(FormValidationRulesMixin) {
        userData = {}
        loading = false
        loadingPassword = false
        password = null
        passwordConfirm = null
        dialog = false
        dataLoaded = false

        @Watch('user')
        onUserChange() {
            if (this.user) {
                this.userData = {...this.userData, ...this.user}
            }
        }

        @Watch('userLocale')
        onUserLocaleChange() {
            callComponentMethod(this, LanguageSwitcher, 'switchLanguage', this.userLocale)
        }

        async mounted() {
            await this.$store.restored
            await this.$nextTick()
            this.userData = {...this.userData, ...this.user}
            if (this.userLocale && this.userLocale !== this.$i18n.locale) {
                callComponentMethod(this, LanguageSwitcher, 'switchLanguage', this.userLocale)
            }
            this.dataLoaded = true
        }

        beforeUnmount() {
            this.userData = {}
        }

        async save(type) {
            const form = type === 'password' ? this.$refs['password-form'] : this.$refs['user-form']
            const {valid} = await form.validate()
            if (valid) {
                try {
                    this.loading = true
                    const data = type === 'password' ? {password: this.password} : this.prepareData()
                    if (authStore.isAgency || authStore.isCorporate || authStore.isTourOperator) {
                        await this.$api.persons.put(this.userData.personId, data)
                    } else {
                        await this.$api.updateCrmPerson.put(this.userData.personId, data)
                    }
                    if (type !== 'password') {
                        authStore.updatePerson(this.userData)
                    }

                    this.$toast.success(this.$t('data_updated'))

                    if (Object.values(appInstance.$conf.public.locales).includes(data.language)) {
                        EventBus.$emit(SWITCH_LANGUAGE, data.language)
                    }
                } catch (error) {
                    console.error(error)
                } finally {
                    this.loading = false
                }
            }
        }

        prepareData() {
            const {prefix, firstName, lastName, citizenshipId, contactPhone, email, language} = this.userData
            return {
                prefix,
                firstName,
                lastName,
                citizenshipId,
                contactPhone,
                email,
                language,
            }
        }

        get user() {
            return authStore.person || {}
        }

        get isB2C() {
            return authStore.isB2C
        }

        get firstName() {
            if (this.userData.personId) {
                return this.userData.firstName ? this.userData.firstName : this.userData.firstNameLocalized
            }
            return null
        }

        set firstName(value) {
            if (this.userData.firstName) {
                this.userData.firstName = value
            } else {
                this.userData.firstNameLocalized = value
            }
        }

        get lastName() {
            if (this.userData.personId) {
                return this.userData.lastName ? this.userData.lastName : this.userData.lastNameLocalized
            }
            return null
        }

        set lastName(value) {
            if (this.userData.lastName) {
                this.userData.lastName = value
            } else {
                this.userData.lastNameLocalized = value
            }
        }

        get countries() {
            return runtimeStore.countries
        }

        get userLocale() {
            return this.user ? this.user.language : null
        }

        get lockEditPrivateClientName() {
            return this.$conf.public.lockEditPrivateClientName
        }

        get totalLoyaltyPoints() {
            return this.user?.totalLoyaltyPoints
        }

        get localesItems() {
            return Object.values(this.$conf.public.locales)
                .map(el => ({
                    value: el,
                    label: this.$t(`languages_local.${el}`),
                }))
                .sort((a, b) => {
                    return a.label.localeCompare(b.label)
                })
        }
    }

    export default UserSettings
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    .shadow {
        opacity: 0.2;
    }

    .wrapper {
        position: relative;
        padding-top: 0;
        margin-top: 0;
    }

    .form-wrapper {
        padding-top: 60px;
    }
    @media #{map.get(settings.$display-breakpoints, 'xs')} {
        .form-wrapper {
            padding-top: 30px;
        }
    }

    .avatar {
        max-width: 5vw;
        min-width: 80px;
        max-height: 5vw;
        min-height: 80px;
        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 50%;
        overflow: hidden;
        top: 0;
    }

    .password-form {
        border-left: 1px solid gray;
        border-left: outset;
    }
</style>
